import React from 'react';

import http from 'api/http';
import { boatAPI } from 'api/requests';

import Select from 'Components/Select';
import { Input, MultiInput } from 'Components/Input';
import AdditionalSection from './AdditionalSection';
import ApplicationActions from 'Components/ApplicationActions';
import Checkbox from 'Components/Checkbox';
import GoogleSelect from 'Components/GoogleSelect';
import { JON_BOAT, NON_POWERED, PONTOON, SINGLE, TWIN, YACHT } from '../../../../../constants/boatApplication';

class BoatSpecifications extends React.Component {
  constructor(props) {
    super(props);

    this.state = {...props.boat.boatSpecifications};

    this.modelYears = [];
    this.inputErrors = {};
    this.invalidInboardLenght = false;
    this.requiredFieldsList = props.boat.specificationsRequiredFieldsList || [];
  }

  componentDidMount() {
    http.get(boatAPI.modelYears)
      .then(({data}) => {
        this.modelYears = data.sort();
        this.forceUpdate();
      });
    this.updateRequiredFieldsList();
  }

  shouldComponentUpdate(nextProps) {
    let updateInfo = {};
    this.requiredFieldsList = nextProps.boat.specificationsRequiredFieldsList || [];

    for (let key in nextProps.boat.boatSpecifications) {
      if (nextProps.boat.boatSpecifications.hasOwnProperty(key)) {
        if (!this.props.boat.boatSpecifications) {
          updateInfo = nextProps.boat.boatSpecifications;
          break;
        }

        if (this.props.boat.boatSpecifications[key] !== nextProps.boat.boatSpecifications[key]) {
          updateInfo[key] = nextProps.boat.boatSpecifications[key];
        }
      }
    }

    if (Object.keys(updateInfo).length) {
      this.setState(updateInfo);
    }

    return true;
  }

  updateRequiredFieldsList = () => {
    const {ribFlag, dieselPower} = this.state;

    const inherentBuoyancyVolumeIndex = this.requiredFieldsList.indexOf('inherentBuoyancyVolume');
    if (ribFlag && inherentBuoyancyVolumeIndex === -1) this.requiredFieldsList.push('inherentBuoyancyVolume');
    else if (!ribFlag && inherentBuoyancyVolumeIndex !== -1) this.requiredFieldsList.splice(inherentBuoyancyVolumeIndex, 1);

    const dieselPowerCodeIndex = this.requiredFieldsList.indexOf('dieselPowerCode');
    if (dieselPower && dieselPowerCodeIndex === -1) this.requiredFieldsList.push('dieselPowerCode');
    else if (!dieselPower && dieselPowerCodeIndex !== -1) this.requiredFieldsList.splice(dieselPowerCodeIndex, 1);
  };

  handleInputChange = targetField => (value, error) => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    if (error || (!value && this.requiredFieldsList.indexOf(targetField) !== -1)) this.inputErrors[targetField] = error || true;
    else delete this.inputErrors[targetField];

    this.setState({[targetField]: value}, () => {
      if (targetField === 'lengthFeet' ||
        targetField === 'lengthInches' ||
        targetField === 'inboardLengthFeet' ||
        targetField === 'inboardLengthInches'
      ) this.validateInboardLength();
    });
  };

  handleSelectYear = year => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    if (!year && this.requiredFieldsList.indexOf('year') !== -1) this.inputErrors['year'] = true;
    else delete this.inputErrors['year'];

    this.setState({year});
  };

  validateInches = value => {
    return (value >= 12);
  };

  validateBoatLength = value => {
    const {boat, maxLengthIs26} = this.props;

    this.validateInboardLength();
    if (maxLengthIs26() && value >= 26) return 'Max length can\'t be more than or equal 26 Ft.';
    if (boat.boatType === YACHT && value < 26) return 'Min length can\'t be less than 26 Ft.';
  };

  validateInboardLength = () => {
    const {lengthFeet, lengthInches, inboardLengthFeet, inboardLengthInches} = this.state;

    const metricInboardLength = (inboardLengthFeet ? inboardLengthFeet / 3.2808 : 0) + (inboardLengthInches ? inboardLengthInches / 39.3696 : 0);
    const metricLength = (lengthFeet ? lengthFeet / 3.2808 : 0) + (lengthInches ? lengthInches / 39.3696 : 0);

    if ((lengthFeet || lengthInches) && metricInboardLength > metricLength) {
      this.inputErrors['inboardLengthFeet'] = 'Boat can\'t be bigger on inside';
      this.inputErrors['inboardLengthInches'] = true;
    } else {
      delete this.inputErrors['inboardLengthFeet'];
      if (!this.validateInches(inboardLengthInches)) delete this.inputErrors['inboardLengthInches'];
    }
    this.forceUpdate();
  };

  validateDisplacement = value => {
    return (value > 9999 ? 'Max displacement is 9999' : false);
  };

  handleSelect = field => value => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    delete this.inputErrors[field];

    this.setState({[field]: value.value || value});
  };

  handleCityAndStateForCapacityTag = cityAndStateForCapacityTag => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    delete this.inputErrors['cityAndStateForCapacityTag'];

    this.setState({cityAndStateForCapacityTag});
  };

  handleMoveToNextStep = () => {
    const {goToStep} = this.props;

    this.saveData()
      .then(id => {
        goToStep('next', true, id);
      });
  };

  handleMoveToArbitraryStep = stepId => {
    const {goToStep, stepIsChanged} = this.props;

    if (!stepIsChanged) {
      return goToStep(typeof stepId === 'string' ? stepId : 'prev', true);
    }

    this.saveData()
      .then(() => {
        goToStep(typeof stepId === 'string' ? stepId : 'prev', true);
      })
      .catch(() => {
        goToStep(typeof stepId === 'string' ? stepId : 'prev', false);
      });
  };

  validateStep = () => {
    return new Promise((resolve, reject) => {
      this.requiredFieldsList.forEach(field => {
        if (this.state[field] === '') this.inputErrors[field] = true;
        if (field === 'lengthFeet' ||
          field === 'lengthInches' ||
          field === 'inboardLengthFeet' ||
          field === 'inboardLengthInches'
        ) this.validateInboardLength();
        if (field.includes('Inch') && this.validateInches(this.state[field])) this.inputErrors[field] = this.validateInches(this.state[field]);
        if (field === 'lengthFeet' && this.validateBoatLength(this.state[field])) this.inputErrors['lengthFeet'] = this.validateBoatLength(this.state[field]);
        if (field === 'displacement' && this.validateDisplacement(this.state[field])) this.inputErrors['displacement'] = this.validateDisplacement(this.state[field]);
      });
      this.forceUpdate();
      if (Object.keys(this.inputErrors).length) reject(); else resolve();
    });
  };

  saveData = () => {
    const {lengthInches, widthInches} = this.state;
    const {boat, updateBoatDetails} = this.props;
    const {id, boatType, engineType} = boat;

    return new Promise((resolve, reject) => {
      this.validateStep()
        .then(() => {
          let data = {...this.state};

          if (data.lengthInches !== undefined) data.lengthInches = lengthInches || 0;
          if (data.widthInches !== undefined) data.widthInches = widthInches || 0;
          if (!data.dieselPowerCode) delete data.dieselPowerCode;
          if (boat.boatType === JON_BOAT) data.bottomContour = 'FLAT';

          let dataPayload = {boatType, engineType, ...data};

          if (isFinite(id)) dataPayload.id = id;

          const info = {};

          http.post(boatAPI.create, dataPayload)
            .then(({data: serverData}) => {
              info.id = serverData.id;
              info.boatSpecifications = {...this.state};
              info.isFloatationTestReportRequired = this.state.lengthFeet < 20;
              updateBoatDetails(info);
              resolve(info.id);
            })
            .catch(() => {
              reject();
            })
        })
        .catch(() => {
          const {state} = this;

          for (let fieldName in state) {
            if (this.inputErrors[fieldName]) {
              state[fieldName] = '';
            }
          }

          this.setState(state, () => {
            const data = {...this.state};
            updateBoatDetails({boatSpecifications: {...data}});
            reject();
          });
        });
    });
  };

  isError = field => {
    return this.inputErrors[field];
  };

  toggleMotorType = () => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    this.setState(prevState => ({
      motorType: prevState.motorType === SINGLE ? TWIN : SINGLE
    }));
  };

  toggleHasElectricPropulsion = () => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    this.setState(prevState => ({
      hasElectricPropulsion: !prevState.hasElectricPropulsion
    }));
  };

  toggleValue = type => () => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    this.setState(prevState => ({[type]: !prevState[type]}), this.updateRequiredFieldsList);
  };

  handleDieselToggle = () => {
    const {dieselPower} = this.state;
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();

    if (!dieselPower) {
      this.requiredFieldsList.push('dieselPowerCode');
      this.setState({dieselPower: true});
    } else {
      this.requiredFieldsList.splice(this.requiredFieldsList.indexOf('dieselPowerCode'), 1);
      delete this.inputErrors['dieselPowerCode'];
      this.setState({dieselPower: false});
    }
  };

  handleDieselTypeChose = value => {
    const {markStepAsRedacted} = this.props;

    markStepAsRedacted();
    delete this.inputErrors['dieselPowerCode'];
    this.setState({dieselPowerCode: value.toUpperCase()});
  };

  render() {
    const {
      name, year, lengthFeet, lengthInches, widthFeet, widthInches, weightOfFinishedBoat, weightOfUnfinishedBoat,
      transformHeightAtCenterline, fuelTankCapacity, motorType, dieselPower, hasElectricPropulsion,
      engineAndDriveWeight, dieselPowerCode, displacement, inboardLengthFeet, inboardLengthInches,
      maxBoatBeamFeet, maxBoatBeamInches, inboardArea, unavInboardArea, transformFlag, ribFlag, combinedAirChambersVolume,
      inherentBuoyancyVolume, subType, bottomContour, cityAndStateForCapacityTag
    } = this.state;
    const {boat, getNavBar, getHeader, stepIsChanged} = this.props;

    const selectedBottomContour = boat.bottomContourList.find(bc => bc.value === bottomContour);
    const selectedEngineSubType = boat.engineSubTypesList.find(bc => bc.value === subType);

    return (
      <React.Fragment>
        {getHeader(this.saveData)}

        {getNavBar(this.handleMoveToNextStep, this.handleMoveToArbitraryStep)}

        <section className='scene_section scene_section_two-columns _border_top_1'>
          <header>Boat Info</header>
          <Input
            label='Model Name *'
            mainWrapperClassName='input__main-wrapper_primary'
            inputClassName='input__input-field_primary'
            maxLength={250}
            error={this.isError('name')}
            value={name}
            onChange={this.handleInputChange('name')}
          />
          <Select
            selectionList={this.modelYears}
            type='primary'
            defaultItem={year && year.toString()}
            disabled={boat.status !== 'DRAFT' && isFinite(boat.id)}
            label='Model Year *'
            error={this.isError('year')}
            handleSelect={this.handleSelectYear}
          />
          <GoogleSelect
            selectionList={this.stateCodes}
            inputClassName='_border_radius_10 _height_40 _width_pc_100 _margin_top_12 _margin_bottom_20'
            dropDownClassName='_border_radius_bottom_left_10 _border_radius_bottom_right_10 _top_65'
            itemClassName='select__drop-down-item'
            defaultItem={cityAndStateForCapacityTag}
            placeholder='Please start typing your City'
            label='City and State *'
            error={this.isError('cityAndStateForCapacityTag')}
            handleSelect={this.handleCityAndStateForCapacityTag}
          />
          <div className='google-powered-icon _position_absolute _right_15 _bottom_15 _height_20 _width_160'></div>
        </section>

        <section className='scene_section scene_section_two-columns boat-specification-section'>
          <header>Boat Specification</header>
          {lengthFeet !== undefined && lengthInches !== undefined && (
            <div className='multi-input-fields-wrapper'>
              <MultiInput
                label='Overall Length *'
                value={lengthFeet}
                onChange={this.handleInputChange('lengthFeet')}
                styles={{ width: 165, height: 40 }}
                error={this.isError('lengthFeet')}
                additionalValidation={this.validateBoatLength}
                units='Ft.'
                isNumeric
                digitsAfterDot={0}
              />
              <MultiInput
                label=''
                value={lengthInches}
                onChange={this.handleInputChange('lengthInches')}
                styles={{ width: 165, height: 40 }}
                error={this.isError('lengthInches')}
                units='In.'
                additionalValidation={this.validateInches}
                isNumeric
              />
            </div>
          )}

          {maxBoatBeamFeet !== undefined && maxBoatBeamInches !== undefined && (
            <div className='multi-input-fields-wrapper'>
              <MultiInput
                label='Maximum Boat Beam * '
                value={maxBoatBeamFeet}
                onChange={this.handleInputChange('maxBoatBeamFeet')}
                styles={{ width: 165, height: 40 }}
                error={this.isError('maxBoatBeamFeet')}
                units='Ft.'
                isNumeric
                digitsAfterDot={0}
              />
              <MultiInput
                label=''
                value={maxBoatBeamInches}
                onChange={this.handleInputChange('maxBoatBeamInches')}
                styles={{ width: 165, height: 40 }}
                error={this.isError('maxBoatBeamInches')}
                units='In.'
                additionalValidation={this.validateInches}
                isNumeric
              />
            </div>
          )}

          {inboardLengthFeet !== undefined && inboardLengthInches !== undefined && (
            <div className='multi-input-fields-wrapper'>
              <MultiInput
                label='Inboard Length *'
                value={inboardLengthFeet}
                onChange={this.handleInputChange('inboardLengthFeet')}
                styles={{ width: 165, height: 40 }}
                error={this.isError('inboardLengthFeet')}
                units='Ft.'
                isNumeric
                digitsAfterDot={0}
              />
              <MultiInput
                label=''
                value={inboardLengthInches}
                onChange={this.handleInputChange('inboardLengthInches')}
                styles={{ width: 165, height: 40 }}
                error={this.isError('inboardLengthInches')}
                units='In.'
                additionalValidation={this.validateInches}
                isNumeric
              />
            </div>
          )}

          {inboardArea !== undefined && unavInboardArea !== undefined && (
            <React.Fragment>
              <Input
                isNumeric
                mainWrapperClassName='input__main-wrapper_primary'
                inputClassName='input__input-field_primary'
                label='Inboard Area *'
                value={inboardArea}
                units='Sq. Ft.'
                error={this.isError('inboardArea')}
                onChange={this.handleInputChange('inboardArea')}
              />
              <Input
                isNumeric
                mainWrapperClassName='input__main-wrapper_primary'
                inputClassName='input__input-field_primary'
                label='Unavailable Inboard Area *'
                value={unavInboardArea}
                units='Sq. Ft.'
                error={this.isError('unavInboardArea')}
                onChange={this.handleInputChange('unavInboardArea')}
              />
            </React.Fragment>
          )}

          {widthFeet !== undefined && widthInches !== undefined && (
            <div className='multi-input-fields-wrapper'>
              <MultiInput
                label='Overall Stern Width *'
                value={widthFeet}
                onChange={this.handleInputChange('widthFeet')}
                styles={{ width: 165, height: 40 }}
                error={this.isError('widthFeet')}
                units='Ft.'
                isNumeric
                digitsAfterDot={0}
              />
              <MultiInput
                label=''
                value={widthInches}
                onChange={this.handleInputChange('widthInches')}
                styles={{ width: 165, height: 40 }}
                units='In.'
                additionalValidation={this.validateInches}
                isNumeric
              />
            </div>
          )}

          {weightOfUnfinishedBoat !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label='Weight of Unfinished Boat (does not include fuel)*'
              value={weightOfUnfinishedBoat}
              units='Lbs.'
              error={this.isError('weightOfUnfinishedBoat')}
              onChange={this.handleInputChange('weightOfUnfinishedBoat')}
            />
          )}
          {bottomContour !== undefined &&
            (boat.bottomContourList.length > 1 ? (
              <Select
                selectionList={boat.bottomContourList}
                mainWrapperClassName='input__main-wrapper_primary'
                inputClassName='input__input-field_primary'
                dropDownClassName='_border_radius_bottom_left_10 _border_radius_bottom_right_10'
                defaultItem={selectedBottomContour}
                itemNameIndicator='name'
                label='Bottom Contour *'
                error={this.isError('bottomContour')}
                handleSelect={this.handleSelect('bottomContour')}
              />
            ) : (
              <div className={'static-input-plug'}>
                <div>Bottom Contour *</div>
                Flat
              </div>
            ))}

          {weightOfFinishedBoat !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label={boat.engineType === NON_POWERED ? 'Boat Dry Weight *' : 'Weight of Finished Boat (does not include fuel)*'}              
              value={weightOfFinishedBoat}
              units='Lbs.'
              error={this.isError('weightOfFinishedBoat')}
              onChange={this.handleInputChange('weightOfFinishedBoat')}
            />
          )}
          {transformHeightAtCenterline !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label='Transom Height at Centerline *'
              value={transformHeightAtCenterline}
              units='In.'
              error={this.isError('transformHeightAtCenterline')}
              onChange={this.handleInputChange('transformHeightAtCenterline')}
            />
          )}
          {engineAndDriveWeight !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label={
                boat.boatType === PONTOON ? 'Engine and Drive Weight *' : 'Engine and Drive Weight including Batt *'
              }
              value={engineAndDriveWeight}
              units='Lbs.'
              error={this.isError('engineAndDriveWeight')}
              onChange={this.handleInputChange('engineAndDriveWeight')}
            />
          )}
          {fuelTankCapacity !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label='Fuel Tank Capacity *'
              value={fuelTankCapacity}
              units='Gals.'
              error={this.isError('fuelTankCapacity')}
              onChange={this.handleInputChange('fuelTankCapacity')}
            />
          )}
          {displacement !== undefined && (
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label='Displacement *'
              value={displacement}
              units='cc'
              additionalValidation={this.validateDisplacement}
              error={this.isError('displacement')}
              onChange={this.handleInputChange('displacement')}
              digitsAfterDot={0}
            />
          )}
        </section>

        {transformFlag !== undefined && (
          <section className='scene_section air-chambers-section'>
            <header>Air Chambers</header>
            <div className='select-wrapper'>
              <Checkbox
                value={transformFlag}
                label='Transom'
                onChange={this.toggleValue('transformFlag')}
                labelType='_margin_left_10'
              />
              <Checkbox
                value={ribFlag}
                label='RIB'
                onChange={this.toggleValue('ribFlag')}
                labelType='_margin_left_30'
              />
            </div>
            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary _margin_right_30'
              inputClassName='input__input-field_primary'
              label='Combined Volume of all Air Chambers *'
              value={combinedAirChambersVolume}
              units=' Cu. Ft.'
              onChange={this.handleInputChange('combinedAirChambersVolume')}
              error={this.isError('combinedAirChambersVolume')}
            />

            <Input
              isNumeric
              mainWrapperClassName='input__main-wrapper_primary'
              inputClassName='input__input-field_primary'
              label='Inherent Buoyancy Volume *'
              value={inherentBuoyancyVolume}
              units=' Cu. Ft.'
              onChange={this.handleInputChange('inherentBuoyancyVolume')}
              disabled={!ribFlag}
              error={this.isError('inherentBuoyancyVolume')}
            />
          </section>
        )}

        {subType !== undefined && (
          <section className='scene_section'>
            <header>Engine</header>
            <Select
              selectionList={boat.engineSubTypesList}
              mainWrapperClassName='_width_330'
              inputClassName='_border_radius_10 _height_40 _width_330 _margin_top_12 _margin_bottom_20'
              dropDownClassName='_border_radius_bottom_left_10 _border_radius_bottom_right_10'
              defaultItem={selectedEngineSubType}
              itemNameIndicator='name'
              label='Sub Type'
              error={this.isError('subType')}
              handleSelect={this.handleSelect('subType')}
            />
          </section>
        )}
        {(motorType !== undefined || dieselPower !== undefined) && (
          <AdditionalSection
            motorType={motorType}
            hasElectricPropulsion={hasElectricPropulsion}
            dieselPower={dieselPower}
            dieselPowerCode={dieselPowerCode}
            isError={this.isError}
            handleDieselToggle={this.handleDieselToggle}
            handleDieselTypeChose={this.handleDieselTypeChose}
            toggleMotorType={this.toggleMotorType}
            toggleHasElectricPropulsion={this.toggleHasElectricPropulsion}
          />
        )}
        <ApplicationActions
          backButtonLabel={boat.status === 'DRAFT' || !isFinite(boat.id) ? 'Prev. Step' : ''}
          saveButtonLabel='Next Step'
          saveButtonAction={
            stepIsChanged
              ? this.handleMoveToNextStep
              : () => {
                this.handleMoveToArbitraryStep('next');
              }
          }
          backButtonAction={this.handleMoveToArbitraryStep}
        />
      </React.Fragment>
    );
  }
}

export default BoatSpecifications;



// WEBPACK FOOTER //
// ./src/app/Scenes/BoatManufacturer/Application/New/BoatSpecifications/index.jsx